import React, { createContext, useContext, useState, useCallback } from 'react';
import { AlertTriangle, CheckCircle, Info, XCircle, X } from 'lucide-react';

const NotificationContext = createContext(null);

export const useToast = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useToast must be used within NotificationProvider');
  return ctx;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);

  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const addNotification = useCallback((type, message, title = null, duration = 5000) => {
    const id = Date.now() + Math.random();
    setNotifications(prev => [...prev, { id, type, message, title }]);
    if (duration > 0) {
      setTimeout(() => removeNotification(id), duration);
    }
    return id;
  }, [removeNotification]);

  const success = useCallback((message, title = 'Succes') => addNotification('success', message, title), [addNotification]);
  const error = useCallback((message, title = 'Erreur') => addNotification('error', message, title, 8000), [addNotification]);
  const warning = useCallback((message, title = 'Attention') => addNotification('warning', message, title), [addNotification]);
  const info = useCallback((message, title = 'Information') => addNotification('info', message, title), [addNotification]);

  return (
    <NotificationContext.Provider value={{ success, error, warning, info }}>
      {children}
      <ToastContainer notifications={notifications} onClose={removeNotification} />
    </NotificationContext.Provider>
  );
};

const iconMap = {
  success: <CheckCircle size={20} className="text-green-600 shrink-0" />,
  error: <XCircle size={20} className="text-red-600 shrink-0" />,
  warning: <AlertTriangle size={20} className="text-orange-600 shrink-0" />,
  info: <Info size={20} className="text-primary shrink-0" />,
};

const bgMap = {
  success: 'bg-green-50 border-green-200',
  error: 'bg-red-50 border-red-200',
  warning: 'bg-orange-50 border-orange-200',
  info: 'bg-primary/5 border-primary/20',
};

const ToastContainer = ({ notifications, onClose }) => {
  if (!notifications.length) return null;

  return (
    <div className="fixed top-4 right-4 z-[60] space-y-2 max-w-md pointer-events-none">
      {notifications.map((n) => (
        <div
          key={n.id}
          className={`${bgMap[n.type] || bgMap.info} border rounded-lg shadow-lg p-4 flex items-start gap-3 animate-slide-in pointer-events-auto`}
        >
          {iconMap[n.type] || iconMap.info}
          <div className="flex-1 min-w-0">
            {n.title && <h4 className="font-semibold text-sm mb-0.5">{n.title}</h4>}
            <p className="text-sm text-gray-700">{n.message}</p>
          </div>
          <button onClick={() => onClose(n.id)} className="text-gray-400 hover:text-gray-600 transition shrink-0">
            <X size={16} />
          </button>
        </div>
      ))}
    </div>
  );
};
